import { z } from 'zod';
import { expectedNoyauMarkSchema, expectedNoyauReadingSchema } from './noyau-expected.js';

/**
 * The MARK of a replay on a session (docs/specs/rejeu.md sections 11.3 and 12,
 * issue #193): the enumerations the mark is made of, the block of a session's
 * detail and its heavy layer.
 *
 * A file of its own, importing nothing but Zod and `noyau-expected.ts` (itself a
 * leaf: the expected noyau of #216), because the list row and the
 * detail of a session (`session-play.ts`, `session-read.ts`) carry the mark
 * while `replay.ts` itself imports `session-play.ts` for the advance of the
 * person: one file holding both would be a cycle, and a cycle of ESM modules
 * reads a schema before it exists.
 */

/* ---------------------------------------------------------- the enumerations */

/**
 * The fidelity of the player, chosen by the operator at the start and kept on
 * the session (spec section 10.2, D7): `strict` says nothing the source does
 * not hold, `completed` may add plausible details and never contradicts it.
 */
export const replayModeSchema = z.enum(['strict', 'completed']);
export type ReplayMode = z.infer<typeof replayModeSchema>;

/** What a source is (spec section 5, D5): a written text, a session's transcript, an interview. */
export const replaySourceKindSchema = z.enum(['written', 'session', 'interview']);
export type ReplaySourceKind = z.infer<typeof replaySourceKindSchema>;

/**
 * Who played a replay (npc.md N2, §7): the SERVER, which plays the person with
 * a model of its own and pays for it, or CLAUDE, prepared by the app or driven
 * by hand through the assistant's tools, as every replay was before the NPC.
 */
export const replayPlayerSchema = z.enum(['server', 'claude']);
export type ReplayPlayer = z.infer<typeof replayPlayerSchema>;

/**
 * The player of a mark that may predate the NPC: a row written before them has
 * none, and reads `claude` - the only player there was (npc.md §16).
 */
export const REPLAY_DEFAULT_PLAYER: ReplayPlayer = 'claude';
export function replayPlayerOf(mark: { readonly player?: ReplayPlayer | undefined }): ReplayPlayer {
  return mark.player ?? REPLAY_DEFAULT_PLAYER;
}

/**
 * The NPC a replay played, as the column Lien draws it (npc.md §13.1): « Marie
 * Curie · v2 ». `versionId` is the version the replay FROZE, which the NPC's
 * own history can have left behind; the NAME is the NPC's current one, and null
 * of the whole block once the NPC was deleted for good (§3.5).
 */
export const replayMarkNpcSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
  })
  .strict();
export type ReplayMarkNpc = z.infer<typeof replayMarkNpcSchema>;

/**
 * The series a replay belongs to (npc.md §13.1), under its NPC: « Petit banc
 * #204, 27 oct. ». `label` is the sentence the server composes, so that the
 * list and the detail word it the same.
 */
export const replayMarkSeriesSchema = z
  .object({ id: z.string().min(1), label: z.string().min(1) })
  .strict();
export type ReplayMarkSeries = z.infer<typeof replayMarkSeriesSchema>;

/**
 * The mark of a replay on a line of the list of sessions (spec section 11.3,
 * section 12): `{ mode, sourceKind, sourceLabel }`, or null on an ordinary
 * session. Readable wherever the session is, even once the replay is closed.
 *
 * Since the NPC (npc.md §13.1, issue #210) it carries four more facts, ALL
 * OPTIONAL, at the régime of every field this mark and its session have grown:
 * a mark built before them reads without them. Absent `player` is `claude`
 * (`replayPlayerOf`), absent `npc` and `series` are null - a replay of a pasted
 * source, or a replay of an NPC begun outside any series - and absent
 * `playerCostEur` is 0.
 *
 * `playerCostEur` is the cost of the PLAYER's own calls (H15), told apart from the
 * device's: the column Coût of Sessions stays the device's.
 */
export const replayMarkSchema = z
  .object({
    mode: replayModeSchema,
    sourceKind: replaySourceKindSchema,
    sourceLabel: z.string().min(1),
    npc: replayMarkNpcSchema.nullable().optional(),
    series: replayMarkSeriesSchema.nullable().optional(),
    player: replayPlayerSchema.optional(),
    playerCostEur: z.number().nonnegative().optional(),
    /**
     * The expected noyau of the NPC version played, against the found one (npc.md
     * §26.6, issue #216), for the pill « attendu » of the list: null or absent when
     * there is no NPC or no expected noyau to say anything of.
     */
    expected: expectedNoyauMarkSchema.nullable().optional(),
  })
  .strict();
export type ReplayMark = z.infer<typeof replayMarkSchema>;

/** The filter `replay` of the list: the replays only, or the sessions without them. */
export const replayFilterSchema = z.enum(['with', 'without']);
export type ReplayFilter = z.infer<typeof replayFilterSchema>;

/**
 * The filter `npc` of the list (npc.md §13.1): the replays of ONE NPC, named by
 * its identifier. Beside `replay`, behind « Plus de filtres », and in the URL.
 */
export const replayNpcFilterSchema = z.string().min(1);
export type ReplayNpcFilter = z.infer<typeof replayNpcFilterSchema>;

/**
 * The fingerprint of the two paragraphs of rules a replay was served (spec
 * section 10.1): the first 8 hexadecimal characters of the SHA-256 of
 * `replayFingerprintInput`, at the régime of `noyauPromptFingerprintSchema`.
 * Computed by the server, which alone holds a hash function.
 */
export const replayFingerprintSchema = z.string().regex(/^[0-9a-f]{8}$/);
export type ReplayFingerprint = z.infer<typeof replayFingerprintSchema>;

/**
 * The origin of a replayed replay (`fromReplay`): its identifier, and the number
 * of the experience version it played, null when that replay is gone (purged
 * with its session, or wiped by a refresh of the dev model).
 */
export const replayOriginSchema = z
  .object({
    replayId: z.string().min(1),
    experienceVersionNumber: z.number().int().positive().nullable(),
  })
  .strict();
export type ReplayOrigin = z.infer<typeof replayOriginSchema>;

/**
 * The block `replay` of a session's detail (spec section 11.3, section 12):
 * everything but the two heavy texts. The shape `get_session` serves by default.
 *
 * `operator` is who started it (the email its `versionAuthor` signs), `playerModel`
 * the model Claude declared (never verified) - or, for a replay the SERVER played,
 * the model that really did -, `sourceChars` the size of the source, `startedAt`
 * the instant of the start.
 *
 * The four facts of the NPC (npc.md §13.2, H29) are optional, as on the mark:
 * `npc`, `series` and `pass` name where the replay came from, `player` who played
 * it, `playerCostEur` what the player cost. `sourceLabel` and `sourceChars` keep
 * saying what was served - for an NPC, its block.
 */
export const sessionReplaySchema = z
  .object({
    mode: replayModeSchema,
    sourceKind: replaySourceKindSchema,
    sourceLabel: z.string().min(1),
    sourceChars: z.number().int().nonnegative(),
    operator: z.string().min(1),
    playerModel: z.string().min(1).nullable(),
    instructionFingerprint: replayFingerprintSchema,
    fromReplay: replayOriginSchema.nullable(),
    startedAt: z.string().datetime(),
    npc: replayMarkNpcSchema.nullable().optional(),
    series: replayMarkSeriesSchema.nullable().optional(),
    /** The rank of the pass in its series, null outside one. */
    pass: z.number().int().positive().nullable().optional(),
    player: replayPlayerSchema.optional(),
    playerCostEur: z.number().nonnegative().optional(),
    /**
     * The expected noyau of the NPC version played, against the found one, with
     * where it falls (npc.md §26.8, issue #216): what `get_session` and the
     * parcours read. Computed at the reading, never stored; null or absent on a
     * replay with no NPC or no expected noyau.
     */
    expected: expectedNoyauReadingSchema.nullable().optional(),
  })
  .strict();
export type SessionReplay = z.infer<typeof sessionReplaySchema>;

/**
 * The HEAVY layer of a replay (spec section 11.3): the text of the source and
 * the instruction as served, behind the layer `replay` of `get_session` so the
 * default reading stays light.
 */
export const sessionReplayLayerSchema = z
  .object({
    sourceText: z.string(),
    instruction: z.string(),
  })
  .strict();
export type SessionReplayLayer = z.infer<typeof sessionReplayLayerSchema>;

/**
 * The block entire - the light facts and the heavy layer - as the screen of a
 * session and the JSON export read it (spec section 12: « le JSON porte le bloc
 * `replay` entier, source et consigne comprises »).
 */
export const sessionReplayFullSchema = sessionReplaySchema.merge(sessionReplayLayerSchema);
export type SessionReplayFull = z.infer<typeof sessionReplayFullSchema>;
